import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Route, RouteProps } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { UnAuthorizedRoute } from '../index';
import Helper from '../../../utils/helper';
import AuthService from '../../../services/auth/Auth.services';
import LocalStorageWrapper from '../../../core/wrappers/browserStorage/localStorage-wrapper';

interface IProps {
    setCurrentUser?: any;
    Component: React.ComponentType<any>;
    res?: any;
    path: string;
    urlId?: boolean;
    exact?: boolean;
}

interface IPropsWithKeycloak extends IProps {
    keycloak: any;
}

interface IState {
    user?: any;
    userIsLogin: boolean;
    isLoading: boolean;
    isAuthenticated: boolean;
    menuLoaded: boolean;
}

const hasValidStoredToken = () => {
    const token = localStorage.getItem('access_token');
    const exp = localStorage.getItem('token_expires');
    if (!token || !exp) return false;
    return Date.now() < Number(exp) - 30_000; // 30sn pay
};

class AuthorizedRouteComponent extends Component<IPropsWithKeycloak, IState> {
    static propTypes = {
        setCurrentUser: PropTypes.any,
        Component: PropTypes.any.isRequired,
        res: PropTypes.any,
        path: PropTypes.string.isRequired,
        urlId: PropTypes.bool,
        exact: PropTypes.bool,
    };

    state = {
        user: null,
        userIsLogin: false,
        isLoading: true,
        isAuthenticated: false,
        menuLoaded: false,
    };

    private authService;
    private menuCheckInterval: any | null = null;

    constructor(props: IPropsWithKeycloak) {
        super(props);
        this.authService = new AuthService();
    }

    async componentDidMount() {
        this.waitForMenu();
    }

    componentWillUnmount() {
        if (this.menuCheckInterval) clearInterval(this.menuCheckInterval);
    }

    waitForMenu = () => {
        this.menuCheckInterval = setInterval(async () => {
            const mainMenu = document.querySelector('.sc-ui-main-menu');
            if (mainMenu && mainMenu.children.length > 0) {
                if (this.menuCheckInterval) clearInterval(this.menuCheckInterval);
                this.setState({ menuLoaded: true });
                await this.checkAuthentication();
            }
        }, 100);

        setTimeout(() => {
            if (this.menuCheckInterval) {
                clearInterval(this.menuCheckInterval);
                if (!this.state.menuLoaded) {
                    this.setState({ menuLoaded: true });
                    this.checkAuthentication();
                }
            }
        }, 1000);
    };

    async checkAuthentication() {
        try {
            const isAuthenticated = await this.checkKeycloakLogin();

            if (isAuthenticated) {
                await this.getCurrentUser();

                let retryCount = 0;
                const maxRetries = 10;
                const retryDelay = 500;

                const tryAuthorization = async () => {
                    const isAuthorized = this.checkAuthorization();
                    if (isAuthorized) {
                        this.setState({ isAuthenticated: true });
                        return;
                    }

                    if (retryCount < maxRetries) {
                        retryCount++;
                        await new Promise((resolve) => setTimeout(resolve, retryDelay));
                        await tryAuthorization();
                    } else {
                        this.setState({ isAuthenticated: false });
                    }
                };

                await tryAuthorization();
            } else {
                localStorage.removeItem('navigateUrls');
                this.authService.login();
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            this.setState({ isAuthenticated: false });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    /**
     * ✅ Artık keycloak.authenticated'a güvenmiyoruz (F5 sonrası false olabilir).
     * Storage token geçerliyse authenticated say.
     */
    async checkKeycloakLogin(): Promise<boolean> {
        try {
            if (hasValidStoredToken()) {
                // token var: login’e zorlama
                return true;
            }

            // token yok/expired: dönüş path kaydet ve login’e hazırlan
            let returnPath = window.location.pathname;
            if (!Helper.isNil(window.location.search)) {
                returnPath += window.location.search;
            }
            LocalStorageWrapper.setItemToLocalStorage('returnPath', returnPath);

            return false;
        } catch (error) {
            console.error('Error checking login:', error);
            return false;
        }
    }

    getCurrentUser() {
        return new Promise<void>((resolve) => {
            this.setState({ user: true, userIsLogin: true }, resolve);
        });
    }

    checkAuthorization() {
        const waitForNavigateUrls = () => {
            const navigateUrls = JSON.parse(localStorage.getItem('navigateUrls') || '[]');
            return navigateUrls && navigateUrls.length > 0;
        };

        if (!waitForNavigateUrls()) return false;

        const navigateUrls = JSON.parse(localStorage.getItem('navigateUrls') || '[]').map((url: any) =>
            url.toLowerCase()
        );

        const currentPath = this.props.path.toLowerCase();
        let pathMatch = currentPath;
        if (currentPath.includes('/:')) {
            pathMatch = currentPath.split('/:')[0];
        }

        return (
            navigateUrls.length === 0 ||
            currentPath === '/home' ||
            navigateUrls.some((url: any) => pathMatch.includes(url))
        );
    }

    render() {
        const { Component, ...rest } = this.props;
        const { isLoading, isAuthenticated, menuLoaded } = this.state;

        if (isLoading || !menuLoaded) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div className="loading-spinner"></div>
                </div>
            );
        }

        const routeProps: RouteProps = {
            ...rest,
            render: (props) =>
                isAuthenticated ? <Component {...props} /> : <UnAuthorizedRoute location={props.location} />,
        };

        return <Route {...routeProps} />;
    }
}

const AuthorizedRouteWrapper: React.FC<IProps> = (props) => {
    const { keycloak, initialized } = useKeycloak();

    if (!initialized) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return <AuthorizedRouteComponent {...props} keycloak={keycloak} />;
};

export const AuthorizedRoute = AuthorizedRouteWrapper;
export default AuthorizedRouteWrapper;
